/**
 * @class ProductForm
 * @extends HTMLElement
 * @description ProductForm custom element for handling product add-to-cart logic. Handles form submission, variant and quantity changes, and communicates with the cart API.
 */
class ProductForm extends HTMLElement {
  popularAddOns = this.closest('main-product')?.popularAddOns || [];

  static initializeKlaviyoBackInStock(productId) {
    const klaviyo = window.klaviyo;
    // Ensure the klaviyo object is available
    if (typeof klaviyo === 'undefined') {
      console.error('Klaviyo object is not defined. Ensure Klaviyo is properly initialized.');
      return;
    }
    // Place the Klaviyo Back in Stock script here, using the productId
    klaviyo.init({
      back_in_stock: {
        selector: '#back-in-stock-button',
        product_id: productId,
      },
    });
  }

  /**
   * @method connectedCallback
   * @description Called when the element is inserted into the DOM. Sets up form references, event listeners, and subscriptions.
   * @returns {void}
   */
  connectedCallback() {
    // Select elements
    this.mainProduct = this.closest('main-product') || document.querySelector('main-product');
    this.form = this.querySelector('form');
    this.quantityInput = this.form?.querySelector('input[name="quantity"]');
    this.variantIdInput = this.form?.querySelector('input[name="id"]');

    const klaviyo = window.klaviyo;

    if (!this.form) this.classList.add('cursor-not-allowed');

    // Get all submit buttons that are part of the form
    const formId = this.form?.getAttribute('id');
    this.submitButtons = this.querySelectorAll(`button[form="${formId}"]`) || [];

    this.form?.addEventListener('submit', (event) => this.onFormSubmit(event));

    //
    if (this.popularAddOns.length > 0) {
      this.calculateTotalAddOnValue();
    }

    // Replace the form with the new variant form when event is triggered
    this.unsubscribeVariantChange = subscribe(
      `variant:changed:${this.dataset.productId}`,
      ({ html }) => {
        const productForm = html.querySelector('product-form').cloneNode(true);
        this.replaceWith(productForm);
        klaviyo?.initializeKlaviyoBackInStock(this.dataset.productId);
      }
    );

    // Update the quantity input when the quantity is changed
    this.unsubscribeQuantityChange = subscribe(
      `quantity:change:${this.dataset.productId}`,
      ({ quantity }) => {
        this.quantityInput.value = quantity;
      }
    );

    // Popular add-ons subscriptions
    this.unsubscribePopularAddOnAdd = subscribe(
      `product-add-on:add:${this.dataset.productId}`,
      (item) => {
        this.popularAddOns = this.mainProduct.getPopularAddOns();
        const index = this.popularAddOns.findIndex((a) => a.id === item.id);
        index >= 0 ? (this.popularAddOns[index] = item) : this.popularAddOns.push(item);
        this.mainProduct.setPopularAddOns(this.popularAddOns);
        this.calculateTotalAddOnValue();
      }
    );

    this.unsubscribePopularAddOnRemove = subscribe(
      `product-add-on:remove:${this.dataset.productId}`,
      (item) => {
        this.popularAddOns = this.mainProduct.getPopularAddOns();
        this.popularAddOns = this.popularAddOns.filter((addOn) => addOn.id !== item.id);
        this.mainProduct.setPopularAddOns(this.popularAddOns);

        this.calculateTotalAddOnValue();
      }
    );

    const productId = this.dataset.productId;
    document.addEventListener('DOMContentLoaded', function () {
      klaviyo?.initializeKlaviyoBackInStock(productId);
    });
  }

  calculateTotalAddOnValue() {
    this.popularAddOns = this.mainProduct.getPopularAddOns();

    // Calculate the total price
    const totalPrice = this.popularAddOns.reduce((total, item) => total + item.price, 0);
    // Calculate the total compare price
    const totalCompareAtPrice = this.popularAddOns.reduce(
      (total, item) => total + item.compareAtPrice,
      0
    );

    // Dispatch the price changed event
    publish(`price:changed:product-add-ons`, {
      priceInCents: totalPrice,
      compareAtPriceInCents: totalCompareAtPrice,
    });
  }

  /**
   * @method disconnectedCallback
   * @description Called when the element is removed from the DOM.
   * @returns {void}
   */
  disconnectedCallback() {
    this.unsubscribeVariantChange();
    this.unsubscribeQuantityChange();
    this.unsubscribePopularAddOnAdd();
    this.unsubscribePopularAddOnRemove();
  }

  /**
   * @method onFormSubmit
   * @description Handle form submission.
   * @param {Event} event - The submit event
   * @returns {Promise<void>}
   */
  async onFormSubmit(event) {
    event.preventDefault();
    this.toggleLoading(true);

    try {
      const formData = this.createFormData();
      const itemData = await this.addToCart(formData);
      this.onSuccess(itemData, formData.items[0].quantity);
    } catch (error) {
      this.onError(error);
    } finally {
      this.toggleLoading(false);
    }
  }

  /**
   * @method toggleLoading
   * @description Toggle loading state on submit buttons
   * @param {boolean} loading - Whether to show loading state
   * @returns {void}
   */
  toggleLoading(loading) {
    this.submitButtons?.forEach((button) => button.classList.toggle('loading', loading));
  }

  /**
   * @method createFormData
   * @description Create form data object for cart API
   * @returns {Object} The form data object with items and properties
   */
  createFormData() {
    const formData = new FormData(this.form);
    const id = formData.get('id');
    const quantity = formData.get('quantity');
    const properties = {};
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.*)\]$/);
      if (match) {
        properties[match[1]] = value;
      }
    }
    const cartObject = {
      items: [
        {
          id,
          quantity,
          properties,
        },
        ...this.popularAddOns,
      ],
    };

    return cartObject;
  }

  /**
   * @method addToCart
   * @description Add item to cart via AJAX
   * @param {Object} formData - The form data object
   * @returns {Promise<Object>} The response data from the cart API
   * @throws {Error} If the request fails
   */
  async addToCart(formData) {
    const response = await fetch(`${routes.cart_add_url}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Failed to add to cart');
    return data;
  }

  /**
   * @method Handle successful add-to-cart
   * @param {Object} itemData - The response data from the cart API
   * @param {number|string} quantity - The quantity added
   * @returns {void}
   */
  onSuccess(itemData, quantity) {
    window.toast.success(window.toast_translations.cart.success);
    publish('cart:added', {
      items: itemData.items,
      quantity,
    });
  }

  /**
   * @method onError
   * @description Handle form error
   * @param {Object} error - The error object
   * @param {string} error.message - The error message
   * @returns {void}
   */
  onError({ message }) {
    console.error(message);
    window.toast.error(message);
  }
}

customElements.define('product-form', ProductForm);
